import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface JobHeroProps {
  badge: { text: string; color: string };
  position: string;
  companyName: string;
  location: string;
  experience: string;
  salary: string;
  workType: string;
  workHours: string;
}

export default function JobHero({ badge, position, companyName, location, experience, salary, workType, workHours }: JobHeroProps) {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = ['채용정보', '인재찾기', '커뮤니티', '기업서비스'];

  return (
    <section className="relative overflow-hidden bg-gradient-purple pt-0">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-neon-purple rounded-full blur-[120px] opacity-15"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-neon-pink rounded-full blur-[100px] opacity-10"></div>
      </div>

      {/* Navigation */}
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="px-10 py-5 flex items-center justify-between">
          <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://public.readdy.ai/ai/img_res/9b5ff483-3eed-4f76-9321-1639ea9d593d.png"
              alt="온니잡 Logo"
              className="h-10 w-auto"
            />
            <span className={`text-2xl font-bold ${scrolled ? 'text-dark-purple' : 'text-white'}`}>
              온니잡
            </span>
          </a>
          <div className="flex items-center gap-14">
            {menuItems.map((item, index) => (
              <a
                key={index}
                href="#"
                className={`text-base font-medium relative group transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-gray-700 hover:text-neon-purple' : 'text-white hover:text-neon-purple'
                }`}
              >
                {item}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-neon-purple group-hover:w-full transition-all duration-300"></span>
              </a>
            ))}
          </div>
          <div className="flex items-center gap-4">
            <button className={`px-7 py-3 rounded-full border transition-all whitespace-nowrap cursor-pointer ${
              scrolled
                ? 'border-gray-300 text-gray-700 hover:border-neon-purple hover:text-neon-purple'
                : 'border-white text-white hover:border-neon-purple hover:text-neon-purple'
            }`}>
              로그인
            </button>
            <button className="px-7 py-3 rounded-full bg-gradient-neon text-white shadow-neon-purple hover:shadow-[0_0_30px_rgba(168,85,247,0.7)] transition-all whitespace-nowrap cursor-pointer">
              회원가입
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Hero Content */}
      <div className="relative z-10 container mx-auto px-20 pt-36 pb-16">
        {/* Breadcrumb */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-2 text-sm text-gray-400 mb-8"
        >
          <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="hover:text-neon-purple transition-colors cursor-pointer">홈</a>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
          <span className="text-gray-500">채용정보</span>
          <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center"></i>
          <span className="text-white">{position}</span>
        </motion.div>

        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <span className={`inline-block ${badge.color} text-dark-deep text-xs font-bold px-4 py-2 rounded-lg mb-5`}>
            {badge.text}
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="text-5xl font-black text-white mb-3"
          style={{ textShadow: '0 0 30px rgba(168, 85, 247, 0.25)' }}
        >
          {position}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="text-lg text-gray-400 mb-10"
        >
          {companyName}
        </motion.p>

        {/* Info Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex items-center gap-8 bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl px-8 py-5"
        >
          {[
            { icon: 'ri-money-dollar-circle-line', label: '급여', value: salary },
            { icon: 'ri-map-pin-line', label: '위치', value: location },
            { icon: 'ri-time-line', label: '근무시간', value: workHours },
            { icon: 'ri-briefcase-line', label: '경력', value: experience },
            { icon: 'ri-user-settings-line', label: '고용형태', value: workType },
          ].map((item, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-neon-purple/15">
                <i className={`${item.icon} text-lg text-neon-purple`}></i>
              </div>
              <div>
                <p className="text-xs text-gray-500">{item.label}</p>
                <p className="text-sm font-semibold text-white">{item.value}</p>
              </div>
              {index < 4 && <div className="w-px h-8 bg-white/10 ml-5"></div>}
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
